import type { CSSProperties } from "react";
import { LockIcon } from "lucide-react";
import Xarrow from "react-xarrows";
import { ILLUSTRATION_COLORS } from "@/constants/values";
import { cn } from "@/components/ui/utils";

export function MultiEntityFeature() {
  const { color1, color3 } = ILLUSTRATION_COLORS;
  return {
    left: {
      title: "Multi-Entity",
      description:
        "Reconcile between multiple entities and your personal accounts and step out of financial silos.",
      button: {
        text: "Learn more",
        onClick: () => {},
      },
    },
    center: {
      backgroundPattern: "dots",
      renderChildren: () => {
        return <>{/* Top thread bot */}</>;
      },
    },
    right: {
      renderChildren: () => {
        return (
          <>
            {" "}
            <div
              // Reduced gap from 2 to 1
              className="flex-nowrap grid grid-cols-[repeat(2,1fr)] grid-rows-[repeat(3,1fr)] gap-6 overflow-clip justify-center items-center max-w-full p-16 pl-20 pr-20 w-full h-full"
            >
              {[
                {
                  icon: "quickbooks",
                  p: 1.5,
                },
                {
                  icon: "sage",
                  p: 1,
                },
                {
                  icon: "xero",
                  p: 1.5,
                },
                {
                  icon: "gmail",
                  p: 1.5,
                },
                {
                  icon: "shopify",
                  p: 1.5,
                },
                {
                  icon: "stripe",
                  p: 0,
                },
              ].map((item, i) => {
                return (
                  <div
                    key={i}
                    // Center horizontally in grid cell
                    className="w-14 h-14 flex justify-center items-center flex-col p-(--p) [border-width:1px] [border-style:solid] border-border rounded-sm shadow-sm ml-auto mr-auto bg-background"
                    style={{ "--p": `${item.p * 8}px` } as CSSProperties}
                  >
                    <div
                      // filter: "grayscale(100%)", // Convert to black and white
                      className="w-full h-full [transition:0.5s_all] [background-image:var(--background-image)] bg-contain bg-no-repeat bg-center"
                      style={
                        {
                          "--background-image": `url('/svgs/integrations/${item.icon}.svg')`,
                        } as CSSProperties
                      }
                    />
                  </div>
                );
              })}
            </div>
          </>
        );
      },
      renderHoverAnimation: (isHovering) => {
        return (
          <>
            <Xarrow
              start="customizable-permissions-top"
              end="customizable-permissions-middle-left"
              color={color1}
              startAnchor={"bottom"}
              strokeWidth={1}
              showHead={false}
              dashness={{
                strokeLen: 8,
                nonStrokeLen: 8,
                animation: 1,
              }}
              path="grid"
            />
            <Xarrow
              start="customizable-permissions-top"
              end="customizable-permissions-middle-right"
              color={color1}
              startAnchor={"bottom"}
              strokeWidth={1}
              showHead={false}
              divContainerStyle={{ transition: "0.3s all" }}
              dashness={{
                strokeLen: 8,
                nonStrokeLen: 8,
                animation: 1,
              }}
              path="grid"
            />
            <Xarrow
              start="customizable-permissions-middle-left"
              end="customizable-permissions-bottom-left"
              color={color1}
              // startAnchor={"right"}
              strokeWidth={1}
              showHead={false}
              dashness={{
                strokeLen: 8,
                nonStrokeLen: 8,
                animation: 1,
              }}
              path="grid"
            />
            <Xarrow
              start="customizable-permissions-middle-right"
              end="customizable-permissions-bottom-right"
              color={color1}
              startAnchor={"right"}
              strokeWidth={1}
              showHead={false}
              dashness={{
                strokeLen: 8,
                nonStrokeLen: 8,
                animation: 1,
              }}
              path="grid"
            />
            <Xarrow
              start="customizable-permissions-middle-left"
              end="customizable-permissions-bottom-middle"
              color={color1}
              startAnchor={"bottom"}
              endAnchor={"top"}
              strokeWidth={1}
              showHead={false}
              dashness={{
                strokeLen: 8,
                nonStrokeLen: 8,
                animation: 1,
              }}
              path="grid"
            />
            {/* Top */}
            <div
              id="customizable-permissions-top"
              className="absolute left-[50%] -top-5 transform-[translate(-50%,0%)] w-16 h-10 p-2 [border-width:1px] [border-style:solid] border-[color-mix(in_srgb,var(--border)_80%,var(--muted-foreground))] rounded bg-background flex flex-col justify-start items-start gap-1 shadow-sm"
            >
              <div className="h-2 w-6 rounded-lg [transition:0.3s_all] bg-[color-mix(in_srgb,var(--border)_50%,var(--muted-foreground)_50%)]" />
              <div className="h-2 w-10 rounded-lg [transition:0.3s_all] bg-[color-mix(in_srgb,var(--border)_80%,var(--muted-foreground)_20%)]" />
            </div>
            {/* Middle */}
            <div
              id="customizable-permissions-middle-left"
              className="absolute left-14.5 w-16 h-10 top-[50%] [transition:0.3s_all] transform-[translate(0%,-50%)] p-2 [border-width:1px] [border-style:solid] border-[color-mix(in_srgb,var(--border)_80%,var(--muted-foreground))] rounded flex flex-col justify-start items-start gap-1 bg-background shadow-sm"
            >
              <div className="h-2 w-8 rounded-lg [transition:0.3s_all] bg-[color-mix(in_srgb,var(--border)_50%,var(--muted-foreground)_50%)]" />
              <div className="h-2 w-6 rounded-lg [transition:0.3s_all] bg-[color-mix(in_srgb,var(--border)_80%,var(--muted-foreground)_20%)]" />
            </div>
            <div
              id="customizable-permissions-middle-right"
              className={cn(
                "absolute w-16 h-10 right-14.5 top-[50%] [transition:0.3s_all] p-1",
                isHovering
                  ? "transform-[translate(0%,-50%)_scale(0.9)]"
                  : "transform-[translate(0%,-50%)_scale(1)]",
                "[border-width:1px] [border-style:solid] border-[initial]",
                isHovering
                  ? "border-[color-mix(in_srgb,var(--border)_20%,var(--muted-foreground)_80%)]"
                  : "border-[color-mix(in_srgb,var(--border)_80%,var(--muted-foreground)_20%)]",
                "rounded flex flex-col justify-start items-start gap-1 bg-background shadow-sm",
              )}
            >
              <LockIcon
                size={16}
                style={{ transition: "0.3s all" }}
                color={isHovering ? color3 : color1}
              />
            </div>
            {/* Bottom */}
            <div
              id="customizable-permissions-bottom-left"
              className="absolute left-5 -bottom-5 w-16 h-10 p-2 [border-width:1px] [border-style:solid] border-[color-mix(in_srgb,var(--border)_80%,var(--muted-foreground))] rounded bg-background [transition:all_0.3s] text-[color-mix(in_srgb,var(--border)_80%,var(--muted-foreground)_20%)] flex flex-col justify-start items-start gap-1 z-100 shadow-sm"
            >
              <div className="h-2 w-10 rounded-lg [transition:0.3s_all] bg-[color-mix(in_srgb,var(--border)_80%,var(--muted-foreground)_20%)]" />
              <div className="h-2 w-6 rounded-lg [transition:0.3s_all] bg-[color-mix(in_srgb,var(--border)_80%,var(--muted-foreground)_20%)]" />
            </div>
            <div
              id="customizable-permissions-bottom-middle"
              className="absolute left-[50%] transform-[translate(-50%,0%)] -bottom-5 w-16 h-10 p-2 [border-width:1px] [border-style:solid] border-[color-mix(in_srgb,var(--border)_80%,var(--muted-foreground))] rounded bg-background [transition:all_0.3s] text-[color-mix(in_srgb,var(--border)_80%,var(--muted-foreground)_20%)] flex flex-col justify-start items-start gap-1 z-100 shadow-sm"
            >
              <div className="h-2 w-6 rounded-lg [transition:0.3s_all] bg-[color-mix(in_srgb,var(--border)_80%,var(--muted-foreground)_20%)]" />
            </div>
            <div
              id="customizable-permissions-bottom-right"
              className={cn(
                "absolute right-5 -bottom-5 w-16 h-10 p-1 [border-width:1px] [border-style:solid] border-[initial]",
                isHovering
                  ? "border-[color-mix(in_srgb,var(--border)_20%,var(--muted-foreground)_80%)]"
                  : "border-[color-mix(in_srgb,var(--border)_80%,var(--muted-foreground)_20%)]",
                "rounded bg-background [transition:all_0.3s] text-[color-mix(in_srgb,var(--border)_80%,var(--muted-foreground)_20%)] flex flex-col justify-start items-start gap-2 z-100 shadow-sm",
                isHovering ? "scale-[0.9]" : "scale-[1]",
              )}
            >
              <LockIcon
                size={16}
                style={{ transition: "0.3s all" }}
                color={isHovering ? color3 : color1}
              />
            </div>
          </>
        );
      },
      backgroundPattern: "dots",
    },
  };
}
